<template>
    <v-flex>
        <v-flex class="file-attachment">
            <v-flex id="post_attachments_unitTests" v-if="alreadyAttachedFiles && alreadyAttachedFiles.length > 0"
                    data-qe-id="already-attached-files_list_area">
                <post-attachments :attachment-list="alreadyAttachedFiles"
                                  :canDelete="$attrs.canDelete"
                                  :deleteAttachmentHandler="$attrs.deleteAttachmentHandler">
                </post-attachments>
            </v-flex>
            <v-flex v-if="files.length > 0">
                <v-layout row
                          wrap
                          v-for="(file, key, index) in files" :key="key" class="file_row">
                    <v-flex xs1 class="typeBlock">
                        <v-icon large
                                :class='iconClassNames(file.type)'>
                        </v-icon>
                        <span :data-qe-id="`attached_file_${index}_size`">
                            {{calculateFileSize(file.size,communityCommonConstant.filesAndMedia)}}
                        </span>
                    </v-flex>
                    <v-flex xs10
                            pr-1
                            class="nameBlock">
                        <strong :data-qe-id="`attached_file_${index}_name`">{{file.name}}</strong>
                    </v-flex>
                    <v-flex xs1
                            text-xs-right
                            class="actionsBlock">
                        <v-btn class="primary_btn transparent small pa-1"
                               :data-qe-id="`attached_file_${index}_remove_btn`"
                               @click="removeFile(key)">
                            <i class="fas fa-trash-alt "></i>
                        </v-btn>
                    </v-flex>
                </v-layout>
            </v-flex>
            <v-layout>
                <v-flex xs6>
                    <span class="error-message">{{errorMessage}}</span>
                </v-flex>
                <v-flex xs6 text-xs-right>
                    <div v-if="files.length < maxFilesToAttach"
                         :title="fileAttachmentConsts.ATTACH_FILE">
                        <file-upload class="card__file-upload"
                                     v-model="attachments"
                                     :multiple="multiple"
                                     :input-id="attachmentId"
                                     :data-qe-id="attachmentId"
                                     ref="postAttachment">
                            <v-icon>link</v-icon>
                        </file-upload>
                    </div>
                </v-flex>
            </v-layout>
        </v-flex>
    </v-flex>
</template>

<script>
    import FileUpload from "vue-upload-component";
    import uniqBy from 'lodash/uniqBy';
    import { iconClassNames, communityCommonConstant, calculateFileSize } from "../../ChapterPage/communityCommon.constant";
    import { fileAttachmentConsts } from './FileAttachmentconstants';
    import PostAttachments from "./PostAttachments";

    export default {
        name: 'FileAttachments',
        props: {
            maxFilesToAttach: {
                type: Number,
                default: 1,
            },
            attachmentCallback: {
                type: Function,
                required: true,
            },
            attachmentId: {
                type: String,
                required: true,
            },
            maxFileSizeLimit: {
                type: Number,
                default: 25 * (1024 * 1024), // 25MB
            },
            maxFileNameLimit: {
                type: Number,
                default: 100, 
            },
            fileSizeFromOtherSources: {
                type: Number,
                default: 0, //bytes
            },
            alreadyAttachedFiles: {
                type: Array,
                default: () => [],
            },
        },
        data: () => {
            return {
                attachments: [],
                files: [],
                communityCommonConstant,
                errorMessage: '',
                fileAttachmentConsts,
                fileTypesSupported: fileAttachmentConsts.FILETYPES_SUPPORTED,
            };
        },
        components: {
            FileUpload,
            PostAttachments,
        },
        methods: {
            iconClassNames,
            calculateFileSize,
            removeFile(key) {
                this.files.splice(key, 1);
                this.attachmentCallback(this.files);
            },
            validateFileSize(files) {
                const bytes = files.reduce((size, file) => {
                    return size + file.size;
                }, 0);
                return (bytes + this.fileSizeFromOtherSources) < this.maxFileSizeLimit;
            },
            validateDuplicateName(files) {
                return uniqBy(files, 'name').length === files.length;
            },
            validateFileType(files) {
                return files.every((file) => {
                    return new RegExp(this.fileTypesSupported.join("|")).test(file.name);
                });
            },
            validateFileNameLength(files) {
                return files.some((file) => {
                    return file.name.length > this.maxFileNameLimit;
                });
            },
            validateFiles(oldFiles, newFiles) {
                if (oldFiles.length + newFiles.length + this.alreadyAttachedFiles.length > this.maxFilesToAttach) {
                    this.errorMessage = `${fileAttachmentConsts.MAX_FILE_COUNT_EXCEEDED}(${this.maxFilesToAttach})`;
                    setTimeout(() => { this.errorMessage = ''; }, fileAttachmentConsts.ERROR_MESSAGE_TIMEOUT_SECONDS);
                    return false;
                } else if (!this.validateFileType([...newFiles])) {
                    this.errorMessage = fileAttachmentConsts.FILE_NOT_SUPPORTED;
                    setTimeout(() => { this.errorMessage = ''; }, fileAttachmentConsts.ERROR_MESSAGE_TIMEOUT_SECONDS);
                    return false;
                } else if (this.validateFileNameLength(newFiles)) {
                    this.errorMessage = fileAttachmentConsts.MAX_FILENAME_COUNT_EXCEEDED;
                    setTimeout(() => { this.errorMessage = ''; }, fileAttachmentConsts.ERROR_MESSAGE_TIMEOUT_SECONDS);
                    return false;
                } else if (!this.validateDuplicateName([...oldFiles, ...newFiles, ...this.alreadyAttachedFiles])) {
                    this.errorMessage = fileAttachmentConsts.FILENAME_ALREADY_EXISTS;
                    setTimeout(() => { this.errorMessage = ''; }, fileAttachmentConsts.ERROR_MESSAGE_TIMEOUT_SECONDS);
                    return false;
                } else if (!this.validateFileSize([...oldFiles, ...newFiles, ...this.alreadyAttachedFiles])) {
                    this.errorMessage = fileAttachmentConsts.FILE_SIZE_EXCEEDED;
                    setTimeout(() => { this.errorMessage = ''; }, fileAttachmentConsts.ERROR_MESSAGE_TIMEOUT_SECONDS);
                    return false;
                }
                return true;
            },
        },
        computed: {
            multiple() {
                return this.maxFilesToAttach > 1;
            },
        },
        watch: {
            attachments(arr) {
                if (arr.length) {
                    if (this.validateFiles(this.files, arr)) {
                        this.files.push(...arr);
                        this.files = uniqBy(this.files, 'name');
                        if (this.maxFilesToAttach === 1) {
                            this.attachmentCallback(this.files[0]);
                        } else {
                            this.attachmentCallback(this.files);
                        }
                    }
                    this.attachments = [];
                }
            },
        },
    };
</script>

<style lang="scss">
    @import "../../../common/styles/_colors.scss";
    $image_icon_color:$ypo-duskblue;

    .file-attachment{
     margin: 15px 5px;

     .file_row
    {
        margin: 15px 2px;
    }

    div.disabled {
        pointer-events: none;
        opacity: 0.5;
    }
    .typeBlock {
        span

    {
        color: $ypo-dark-background-gray;
        font-size: 9px;
        font-weight: bold;
        letter-spacing: 0.59px;
        line-height: 12px;
        display: block;
        text-transform: uppercase;
    }

    i {
        color: $ypo-dark-background-gray;
    }

    }

    .nameBlock {
        strong {
        color: $ypo_blue;
        font-size: 14px;
        font-weight: bold;
        letter-spacing: 0.19px;
        line-height: 26px;
        word-break: break-all;
    }
    }
    .error-message {
        color: red;
        margin: 8px;
    }
    .card__file-upload {
        cursor: pointer;
        & label{
            cursor: pointer;
               }
    }
    }
</style>
